import { Component, defineElement } from '@theme/component';

const EXCLUDED_TAGS = ['loyalty-gift', 'gift', 'gwp', 'gift-with-purchase', 'free-sample'];

export function buildSearchUrl(term) {
  return `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product,article&resources[limit]=10&resources[options][fields]=title,product_type,variants.title,body,tag`;
}

export function buildHiddenArticlesUrl(term) {
  return `${buildSearchUrl(term).replace('/search/suggest.json?', '/search/suggest?')}&section_id=search-hidden-articles`;
}

export async function fetchHiddenArticleIds(term, signal) {
  try {
    const response = await fetch(buildHiddenArticlesUrl(term), { signal });
    if (!response.ok) return new Set();
    const html = await response.text();
    const json = html.match(/<script[^>]*data-hidden-article-ids[^>]*>([\s\S]*?)<\/script>/)?.[1];
    return new Set(JSON.parse(json || '[]').map(String));
  } catch {
    return new Set();
  }
}

export function filterHiddenArticles(articles, hiddenIds) {
  if (!hiddenIds.size) return articles;
  return articles.filter((article) => !hiddenIds.has(String(article.id)));
}

export function flattenSearchProducts(products) {
  const items = [];
  products.forEach((product) => {
    if (EXCLUDED_TAGS.some((tag) => product.tags.includes(tag))) return;
    if (product.variants && product.variants.length > 0) {
      product.variants.forEach((variant) => {
        items.push({
          id: variant.id,
          title: product.title,
          variantTitle: variant.title,
          price: variant.price,
          image: variant.featured_image?.url || product.featured_image?.url || '',
          url: variant.url,
          tags: product.tags,
          type: 'product',
        });
      });
    } else {
      items.push({
        id: product.id,
        title: product.title,
        variantTitle: null,
        price: product.price,
        image: product.featured_image?.url || '',
        url: product.url,
        tags: product.tags,
        type: 'product',
      });
    }
  });
  return items;
}

export function resultStatusMessage(term, count) {
  if (count === 0) return `No results for ${term}.`;
  return `${count} ${count === 1 ? 'result' : 'results'} for ${term}.`;
}

export function searchResultDetail(item, index, type, term) {
  return {
    searchTerm: term,
    resultType: type,
    resultTitle: item.title,
    resultPosition: index + 1,
    resultId: item.id,
    resultVariant: item.variantTitle || null,
    resultVariantId: item.variantId || null,
    resultUrl: item.url,
  };
}

export function getSearchFocusableElements(panel) {
  return Array.from(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((element) => {
    return element.tabIndex >= 0 && !element.closest('[inert], [aria-hidden="true"]') && element.getClientRects().length > 0;
  });
}

function whenSearchSwiperReady(callback, signal) {
  if (typeof window.Swiper === 'function') {
    callback();
    return;
  }
  window.addEventListener('yise:swiper-ready', callback, { once: true, signal });
}

function destroySearchSwiper(region) {
  region.controlsAbort?.abort();
  region.controlsAbort = null;
  region.pending = false;
  if (!region.swiper) return;
  try {
    region.swiper.destroy(true, true);
  } catch (error) {}
  region.swiper = null;
}

export function searchSwiperOptions(type, element) {
  const options = {
    grabCursor: true,
    spaceBetween: 20,
    threshold: 20,
    passiveListeners: true,
    shortSwipes: true,
    touchAngle: 75,
    touchMoveStopPropagation: true,
    slidesOffsetAfter: 0,
    watchOverflow: true,
    watchSlidesProgress: true,
    a11y: type === 'product' ? {
      containerMessage: 'Product results',
      containerRoleDescriptionMessage: 'carousel',
      itemRoleDescriptionMessage: 'slide',
      slideRole: 'listitem',
      slideLabelMessage: 'Product \u007b\u007bindex\u007d\u007d of \u007b\u007bslidesLength\u007d\u007d',
      prevSlideMessage: 'Previous products',
      nextSlideMessage: 'Next products',
    } : {
      containerMessage: 'Article results',
      containerRoleDescriptionMessage: 'carousel',
      itemRoleDescriptionMessage: 'slide',
      slideRole: 'listitem',
      slideLabelMessage: 'Article \u007b\u007bindex\u007d\u007d of \u007b\u007bslidesLength\u007d\u007d',
      prevSlideMessage: 'Previous articles',
      nextSlideMessage: 'Next articles',
    },
    mousewheel: { releaseOnEdges: false, forceToAxis: true, sensitivity: 0.25 },
    navigation: {
      nextEl: element.querySelector('.swiper-button-next'),
      prevEl: element.querySelector('.swiper-button-prev'),
    },
    breakpoints: {
      320: { slidesPerView: 2, spaceBetween: 10 },
      676: { slidesPerView: 3, spaceBetween: 10 },
      992: { slidesPerView: 4 },
    },
  };
  if (type === 'product') options.cssMode = false;
  return options;
}

export class YiseSearch extends Component {
  productItems = [];
  articleItems = [];
  searchRequestId = 0;
  clearTimers = new Set();
  requests = new Set();
  cardItems = new WeakMap();

  ready() {
    const firstConnection = !this.input;
    this.input = this.querySelector('[data-search-input]');
    this.main = this.querySelector('[data-search-main]');
    this.message = this.querySelector('[data-search-message]');
    this.status = this.querySelector('[data-search-status]');
    this.searchTerm = this.input.value;
    this.regions ||= Object.fromEntries(['product', 'article'].map((type) => {
      const shell = this.querySelector(`[data-search-region="${type}"]`);
      return [type, {
        shell,
        element: shell.querySelector('[role="region"]'),
        wrapper: shell.querySelector('[role="list"]'),
        template: this.querySelector(`[data-search-card-template="${type}"]`),
      }];
    }));
    this.listen(this.input, 'input', this.onInput);
    this.listen(this.input, 'compositionstart', this.onCompositionStart);
    this.listen(this.input, 'compositionend', this.onCompositionEnd);
    this.listen(this.input, 'change', this.onCompositionEnd);
    this.listen(this.querySelector('[data-search-close]'), 'click', this.closeSearch);
    this.listen(document, 'keydown', this.handleKeydown);
    this.listen(this, 'click', this.onResultClick);
    for (const type of ['product', 'article']) {
      const region = this.regions[type];
      if (this[`${type}Items`].length === 0) region.shell.remove();
      else this.initializeSwiper(type);
    }
    if (firstConnection && this.searchTerm.trim()) this.onInput();
  }

  teardown() {
    clearTimeout(this.debounceTimer);
    for (const timer of this.clearTimers) clearTimeout(timer);
    this.clearTimers.clear();
    this.searchRequestId += 1;
    for (const request of this.requests) request.abort();
    this.requests.clear();
    this.main?.setAttribute('aria-busy', 'false');
    for (const region of Object.values(this.regions || {})) destroySearchSwiper(region);
  }

  onCompositionStart = () => { this.input.composing = true; };

  onCompositionEnd = () => {
    if (!this.input.composing) return;
    this.input.composing = false;
    this.input.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
  };

  onInput = () => {
    const previousTerm = this.searchTerm;
    if (!this.input.composing) this.searchTerm = this.input.value;
    if (!this.searchTerm && previousTerm !== this.searchTerm) {
      this.status.textContent = '';
      const timer = setTimeout(() => {
        this.clearTimers.delete(timer);
        this.productItems = [];
        this.articleItems = [];
        this.renderResults();
      }, 1000);
      this.clearTimers.add(timer);
    }
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.search(), 300);
  };

  async search() {
    const term = (this.searchTerm || '').trim();
    if (term === '') {
      this.main.setAttribute('aria-busy', 'false');
      this.message.textContent = 'Please enter a search term';
      this.status.textContent = this.message.textContent;
      return;
    }
    this.lastSearch = term;
    this.main.setAttribute('aria-busy', 'true');
    const requestId = ++this.searchRequestId;
    const request = new AbortController();
    this.requests.add(request);
    document.dispatchEvent(new CustomEvent('search:query', { detail: { searchTerm: term } }));
    try {
      const hiddenArticleIds = fetchHiddenArticleIds(term, request.signal);
      const response = await fetch(buildSearchUrl(term), { signal: request.signal });
      if (!response.ok) throw new Error('Network response was not ok');
      const suggestions = await response.json();
      const hiddenIds = await hiddenArticleIds;
      if (requestId !== this.searchRequestId) return;
      this.productItems = flattenSearchProducts(suggestions.resources.results.products);
      this.articleItems = filterHiddenArticles(suggestions.resources.results.articles, hiddenIds);
      this.main.setAttribute('aria-busy', 'false');
      this.message.textContent = '';
      this.status.textContent = resultStatusMessage(term, this.productItems.length + this.articleItems.length);
      document.dispatchEvent(new CustomEvent('search:results', {
        detail: { searchTerm: term, productCount: this.productItems.length, articleCount: this.articleItems.length },
      }));
      this.renderResults();
    } catch (error) {
      if (requestId !== this.searchRequestId) return;
      this.main.setAttribute('aria-busy', 'false');
      this.message.textContent = 'An error occurred while fetching search results. Please try again later.';
      this.status.textContent = this.message.textContent;
      console.error('Fetch error:', error);
    } finally {
      this.requests.delete(request);
    }
  }

  updateCard(card, item, index, type) {
    card.setAttribute('data-title', item.title);
    card.querySelector('[data-search-title]').textContent = ` ${item.title} `;
    const image = card.querySelector('img');
    const imageUrl = type === 'product' ? item.image : item.featured_image.url;
    if (image.getAttribute('src') !== imageUrl) image.setAttribute('src', imageUrl);
    for (const link of card.querySelectorAll('a')) {
      link.setAttribute('href', item.url);
      link.setAttribute('data-click-product', item.title);
      link.setAttribute('data-click-productid', item.id);
      if (item.variantTitle) link.setAttribute('data-click-variant', item.variantTitle);
      else link.removeAttribute('data-click-variant');
      if (item.variantId) link.setAttribute('data-click-variantid', item.variantId);
      else link.removeAttribute('data-click-variantid');
      this.cardItems.set(link, { item, index, type });
    }
    if (type !== 'product') return;
    let badge = card.querySelector('[data-search-badge]');
    const tag = item.tags?.find((entry) => entry.startsWith('badge_'));
    if (!badge && tag) {
      badge = this.regions.product.template.content.querySelector('[data-search-badge]').cloneNode(true);
      card.querySelector('figure a').append(badge);
    }
    if (badge && tag) badge.textContent = ` ${tag.replace('badge_', '')} `;
    else badge?.remove();
    let subtitle = card.querySelector('[data-search-subtitle]');
    if (!subtitle && item.variantTitle && item.variantTitle !== 'Default Title') {
      subtitle = this.regions.product.template.content.querySelector('[data-search-subtitle]').cloneNode(true);
      card.querySelector('[data-search-title]').after(subtitle);
    }
    if (subtitle && item.variantTitle && item.variantTitle !== 'Default Title') {
      subtitle.querySelector('[data-search-variant-title]').textContent = item.variantTitle;
    } else subtitle?.remove();
  }

  renderResults() {
    for (const type of ['product', 'article']) {
      const items = this[`${type}Items`];
      const region = this.regions[type];
      destroySearchSwiper(region);
      const existing = new Map(Array.from(region.wrapper.children).map((card) => [this.cardItems.get(card.querySelector('a'))?.item.id, card]));
      let previous = null;
      for (const [index, item] of items.entries()) {
        let card = existing.get(item.id);
        if (card) existing.delete(item.id);
        else card = document.importNode(region.template.content.firstElementChild, true);
        this.updateCard(card, item, index, type);
        const target = previous ? previous.nextElementSibling : region.wrapper.firstElementChild;
        if (target !== card) region.wrapper.insertBefore(card, target);
        previous = card;
      }
      for (const card of existing.values()) card.remove();
      if (items.length === 0) {
        region.shell.remove();
        continue;
      }
      region.shell.hidden = false;
      if (!region.shell.parentElement) {
        if (type === 'product') this.main.insertBefore(region.shell, this.regions.article.shell.parentElement ? this.regions.article.shell : null);
        else this.main.append(region.shell);
      }
      this.initializeSwiper(type);
    }
    const hasResults = this.productItems.length > 0 || this.articleItems.length > 0;
    this.style.height = hasResults ? '100vh' : 'auto';
    document.body.classList.toggle('no-scroll', hasResults);
  }

  initializeSwiper(type) {
    const region = this.regions[type];
    if (region.swiper || region.pending || !region.wrapper.children.length) return;
    region.pending = true;
    whenSearchSwiperReady(() => {
      region.pending = false;
      if (!this.isConnected || region.swiper || !region.shell.isConnected || !region.wrapper.children.length) return;
      region.swiper = new window.Swiper(region.element, searchSwiperOptions(type, region.element));
      this.bindSwiperControls(region);
    }, this.abortController.signal);
  }

  bindSwiperControls(region) {
    const swiper = region.swiper;
    const controls = Array.from(region.shell.querySelectorAll('[data-search-swiper-control]'));
    const controlsAbort = new AbortController();
    region.controlsAbort = controlsAbort;
    let activatedControl = null;
    const syncSlides = () => {
      const firstVisibleSlide = swiper.activeIndex;
      const lastVisibleSlide = firstVisibleSlide + Math.ceil(Number(swiper.params.slidesPerView) || 1);
      Array.from(swiper.slides).forEach((slide, index) => {
        const visible = index >= firstVisibleSlide && index < lastVisibleSlide;
        slide.toggleAttribute('inert', !visible);
        slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });
    };
    const updateControls = () => {
      controls.forEach((control) => {
        const disabled = swiper.isLocked || (control.dataset.searchSwiperControl === 'previous' ? swiper.isBeginning : swiper.isEnd);
        control.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      });
    };
    for (const control of controls) {
      control.addEventListener('click', (event) => {
        if (control.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        activatedControl = control;
      }, { capture: true, signal: controlsAbort.signal });
      if (control.classList.contains('swiper-button-prev') || control.classList.contains('swiper-button-next')) continue;
      control.addEventListener('click', () => {
        if (control.getAttribute('aria-disabled') === 'true') return;
        if (control.dataset.searchSwiperControl === 'previous') swiper.slidePrev();
        else swiper.slideNext();
      }, { signal: controlsAbort.signal });
    }
    swiper.on('slideChange', () => {
      syncSlides();
      updateControls();
      if (activatedControl) {
        const control = activatedControl;
        activatedControl = null;
        window.requestAnimationFrame(() => {
          if (!controlsAbort.signal.aborted && control.isConnected) control.focus();
        });
      }
    });
    swiper.on('lock', updateControls);
    swiper.on('unlock', updateControls);
    swiper.on('resize', () => { syncSlides(); updateControls(); });
    syncSlides();
    updateControls();
  }

  handleKeydown = (event) => {
    if (!document.body.classList.contains('search-open') || !this.contains(event.target)) return;
    if (event.key === 'Tab') {
      const focusable = getSearchFocusableElements(this);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) event.preventDefault();
      else if (event.shiftKey && event.target === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && event.target === last) { event.preventDefault(); first.focus(); }
      return;
    }
    if (event.key !== 'Escape') return;
    event.preventDefault();
    if (event.target !== this.input) { this.input.focus(); return; }
    this.closeSearch();
  };

  closeSearch = () => {
    const trigger = document.getElementById(document.body.dataset.searchTrigger);
    this.searchRequestId += 1;
    document.dispatchEvent(new CustomEvent('search:close', {
      detail: {
        searchTerm: this.searchTerm,
        hadResults: this.productItems.length > 0 || this.articleItems.length > 0,
        productCount: this.productItems.length,
        articleCount: this.articleItems.length,
      },
    }));
    this.productItems = [];
    this.articleItems = [];
    this.main.setAttribute('aria-busy', 'false');
    this.status.textContent = '';
    this.renderResults();
    document.body.classList.remove('no-scroll', 'search-open');
    queueMicrotask(() => { if (trigger?.isConnected) trigger.focus(); });
  };

  onResultClick = (event) => {
    const link = event.target.closest('a');
    const result = this.cardItems.get(link);
    if (!result) return;
    document.dispatchEvent(new CustomEvent('search:result_click', {
      detail: searchResultDetail(result.item, result.index, result.type, this.lastSearch || this.searchTerm),
    }));
  };
}

defineElement('yise-search', YiseSearch);
